import { useState, useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/lib/api";

export const Route = createFileRoute("/me/mfa/enroll")({
  component: MFAEnrollPage,
});

type Step = "loading" | "setup" | "confirm" | "success" | "already_enrolled" | "error";

interface SetupData {
  secret: string;
  otpauth_url: string;
}

function MFAEnrollPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("loading");
  const [setupData, setSetupData] = useState<SetupData | null>(null);
  const [code, setCode] = useState("");
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [confirmPending, setConfirmPending] = useState(false);

  useEffect(() => {
    async function setup() {
      const result = await api.post<SetupData>("/v1/me/mfa/setup", {});
      if (result.status === 409) {
        setStep("already_enrolled");
        return;
      }
      if (!result.ok || !result.body) {
        setStep("error");
        return;
      }
      setSetupData(result.body);
      setStep("setup");
    }
    void setup();
  }, []);

  async function handleConfirm(e: React.FormEvent) {
    e.preventDefault();
    if (code.length !== 6 || confirmPending) return;
    setConfirmError(null);
    setConfirmPending(true);
    try {
      const result = await api.post<void>("/v1/me/mfa/enable", { totp_code: code });
      if (result.ok) {
        setStep("success");
        return;
      }
      if (result.status === 409) {
        setStep("already_enrolled");
        return;
      }
      setConfirmError("invalid code — try again");
      setCode("");
    } finally {
      setConfirmPending(false);
    }
  }

  if (step === "loading") {
    return (
      <div className="mfa-enroll-stage">
        <p className="mfa-enroll__voice">loading…</p>
      </div>
    );
  }

  if (step === "error") {
    return (
      <div className="mfa-enroll-stage">
        <p className="mfa-enroll__voice" style={{ color: "var(--color-death)" }}>
          setup failed. try again.
        </p>
      </div>
    );
  }

  if (step === "already_enrolled" || step === "success") {
    return (
      <div className="mfa-enroll-stage" data-testid="mfa-success">
        <div className="mfa-enroll-panel">
          <div className="mfa-enroll-panel__eyebrow">MFA</div>
          <h1 className="mfa-enroll-panel__title">Authenticator active.</h1>
          <p className="mfa-enroll-panel__voice">My admin session is secured. You may proceed.</p>
          <button
            type="button"
            className="access-btn access-btn--energy"
            onClick={() => void navigate({ to: "/admin" })}
          >
            CONTINUE TO ADMIN
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mfa-enroll-stage">
      <div className="mfa-enroll-panel">
        <div className="mfa-enroll-panel__eyebrow">
          <span className="num">01</span>
          <span>SCAN QR CODE</span>
        </div>
        <h1 className="mfa-enroll-panel__title">Link your authenticator.</h1>
        <p className="mfa-enroll-panel__voice">Scan the code below with your authenticator app.</p>

        {setupData && (
          <>
            <div className="mfa-enroll-panel__qr" data-testid="qr-code">
              <QRCodeSVG
                value={setupData.otpauth_url}
                size={160}
                bgColor="var(--color-canvas)"
                fgColor="var(--color-fg-1)"
                aria-label="QR code for authenticator setup"
              />
            </div>
            <details className="mfa-enroll-panel__manual">
              <summary className="mfa-enroll-panel__manual-toggle">
                can&rsquo;t scan? use the key
              </summary>
              <code
                className="mfa-enroll-panel__secret"
                data-testid="mfa-secret"
                aria-label="TOTP secret key"
              >
                {setupData.secret}
              </code>
            </details>
          </>
        )}

        <div className="mfa-enroll-panel__divider" />

        <div className="mfa-enroll-panel__eyebrow" style={{ marginTop: "var(--space-6)" }}>
          <span className="num">02</span>
          <span>CONFIRM CODE</span>
        </div>
        <p className="mfa-enroll-panel__voice">Enter a 6-digit code from the app.</p>

        <form className="mfa-enroll-panel__form" onSubmit={(e) => void handleConfirm(e)} noValidate>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            autoComplete="one-time-code"
            placeholder="000000"
            className="mfa-enroll-panel__code-input"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            aria-label="TOTP code"
            aria-describedby={confirmError ? "confirm-error" : undefined}
            data-testid="totp-input"
          />
          {confirmError && (
            <p
              id="confirm-error"
              className="mfa-enroll-panel__error"
              role="alert"
              data-testid="confirm-error"
            >
              {confirmError}
            </p>
          )}
          <button
            type="submit"
            className="access-btn access-btn--data"
            disabled={code.length !== 6 || confirmPending}
            data-testid="confirm-btn"
          >
            {confirmPending ? "VERIFYING…" : "CONFIRM"}
          </button>
        </form>
      </div>
    </div>
  );
}
